import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { FC } from 'react';

import { PrivateAccess, useKeycloakContext } from '@wf/keycloak-axios-provider';
import { PageHeader, Tooltip } from '@wf/sw-ui-kit';
import { Button, Icon } from '@wf/components';

import ContentContainer from '../../../../components/ContentContainer';
import { removeQueryMessage } from '../../../License/store/slice';
import LicensesTable from '../../components/LicensesTable';

import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import useLicenseTypeNature from '../../../../hooks/useLicenseTypeNature';
import { declarantAndTraderRoles } from '../../../../constants';
import { LicenseTypeNature } from '../../../../types';
import { LPCO_TABLE_NAME } from '../../constants';

import { resetLicensesData, resetNewLicensesData } from '../../store/slices';

import styles from './Licenses.module.scss';

const Licenses: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const licenseTypeNature = useLicenseTypeNature();
  const licenseAbbreviation = useLicenseAbbreviation();

  const { getUserData } = useKeycloakContext();
  const { username }: IUserInfo = getUserData();

  useEffect(() => {
    dispatch(removeQueryMessage());

    return () => {
      dispatch(resetLicensesData({ licenseTypeNature }));
      dispatch(resetNewLicensesData({ licenseTypeNature }));
    };
  }, [dispatch, licenseTypeNature]);

  return (
    <>
      <PageHeader
        actions={
          <PrivateAccess roles={declarantAndTraderRoles}>
            <Button leftIcon={<Icon name="ic_add" />} onClick={() => navigate('create')}>
              {t('Fill New Application')}
            </Button>
          </PrivateAccess>
        }
        title={t(licenseAbbreviation)}
      />
      <ContentContainer>
        <div className={styles.tableContainer}>
          <h2 className="fs-20 fw-semibold">
            <Tooltip
              tooltipContent={t('{{licenseAbbreviation}} application requests requiring processing.', {
                licenseAbbreviation,
              })}
              placement="right"
            >
              {t('New Requests')}
            </Tooltip>
          </h2>
          <LicensesTable
            tableName={`${username}::${licenseTypeNature ?? LicenseTypeNature.LPCO}::${LPCO_TABLE_NAME.NEW_REQUESTS}`}
            isNewLicenses
          />
        </div>
        <div className={styles.tableContainer}>
          <h2 className="fs-20 fw-semibold">
            <Tooltip
              tooltipContent={t('{{licenseAbbreviation}} application requests not requiring processing', {
                licenseAbbreviation,
              })}
              placement="right"
            >
              {t('{{licenseAbbreviation}} List', { licenseAbbreviation: t(licenseAbbreviation) })}
            </Tooltip>
          </h2>
          <LicensesTable
            tableName={`${username}::${licenseTypeNature ?? LicenseTypeNature.LPCO}::${LPCO_TABLE_NAME.LIST}`}
            isNewLicenses={false}
          />
        </div>
      </ContentContainer>
    </>
  );
};

export default Licenses;
